import { useCallback, useMemo } from "react";

import { useAssets } from "./useAssets";

interface UseAssetReturnsProps {
  itemId?: string;
  productName?: string;
  instanceName?: string;
  assetCategory?: "SOFTWARE" | "HARDWARE";
  skipValidation?: boolean;
}

/**
 * A specialized hook for handling asset returns
 * This hook builds on top of useAssets to provide a focused API for return operations
 *
 * @param {UseAssetReturnsProps} props - Configuration for the hook
 * @returns Object containing return-related functionality and state
 */
export const useAssetReturns = ({
  itemId,
  productName,
  instanceName,
  assetCategory = "SOFTWARE",
  skipValidation,
}: UseAssetReturnsProps) => {
  const { assetStatus, effectiveHasLicense, isLoading, error, returnAsset, checkAssetStatus } = useAssets({
    itemId,
    productName,
    instanceName,
    assetCategory,
    skipValidation,
  });

  // Memoize the return eligibility to avoid unnecessary recalculations
  const canReturn = useMemo(() => {
    // An asset can be returned if:
    // 1. It has an effective license (for Smartsheet: only licensed_user, not connected_user)
    // 2. There's no return already in progress
    return effectiveHasLicense === true && assetStatus?.returnStatus?.inProgress !== true;
  }, [effectiveHasLicense, assetStatus?.returnStatus?.inProgress]);

  // Memoize the return status information
  const returnStatus = useMemo(() => {
    return assetStatus?.returnStatus;
  }, [assetStatus]);

  // Provide a more focused return function with additional validation
  const handleReturn = useCallback(async () => {
    if (!canReturn) {
      // Asset is not eligible for return
      return;
    }

    try {
      await returnAsset();
      return true;
    } catch (err) {
      // Return operation failed
      return false;
    }
  }, [canReturn, returnAsset]);

  return {
    // Return status information
    canReturn,
    returnStatus,
    inProgress: assetStatus?.returnStatus?.inProgress || false,
    returnRequests: assetStatus?.returnStatus?.returnRequests || [],

    // Actions
    returnAsset: handleReturn,
    checkAssetStatus,

    // Base state
    hasLicense: assetStatus?.hasLicense || false,
    licenseType: assetStatus?.licenseType,
    isLoading,
    error,
  };
};
